import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, ShieldCheck, MapPin, Phone, FileText, X, Check, AlertTriangle, Info, Star, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SYMPTOM_CHIPS = ["Healthy", "Skin/Allergies", "Joint/Hip", "Digestive", "Respiratory", "Diagnosed condition"];

export default function InsurancePage({ show }) {
  const [step, setStep] = useState("quiz"); // quiz | results
  const [species, setSpecies] = useState("dog");
  const [breed, setBreed] = useState("");
  const [ageYears, setAgeYears] = useState(2);
  const [gender, setGender] = useState("male");
  const [symptoms, setSymptoms] = useState(["Healthy"]);
  const [breeds, setBreeds] = useState([]);
  const [breedSearch, setBreedSearch] = useState("");
  const [showBreedList, setShowBreedList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [compareSet, setCompareSet] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showBookCall, setShowBookCall] = useState(false);
  const [bookForm, setBookForm] = useState({ name: "", email: "", phone: "", date: "", time: "" });
  const [booked, setBooked] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);

  useEffect(() => {
    axios.get(`${API}/insurance/breeds`).then((r) => setBreeds(r.data.breeds || [])).catch(() => {});
  }, []);

  const filteredBreeds = breeds.filter((b) => b.toLowerCase().includes(breedSearch.toLowerCase())).slice(0, 8);
  const ageMonths = Math.round(ageYears * 12);
  const hasPED = symptoms.includes("Diagnosed condition");
  const wantsOPD = symptoms.some((s) => ["Skin/Allergies", "Digestive", "Respiratory"].includes(s));
  const wantsTPL = false;

  const toggleSymptom = (s) => {
    if (s === "Healthy") { setSymptoms(["Healthy"]); return; }
    setSymptoms((prev) => {
      const without = prev.filter((x) => x !== "Healthy" && x !== s);
      return prev.includes(s) ? (without.length ? without : ["Healthy"]) : [...without, s];
    });
  };

  const runCompare = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/insurance/compare`, {
        species, breed: breed || breedSearch, age_months: ageMonths, gender,
        symptoms: symptoms.filter((s) => s !== "Healthy").map((s) => s.toLowerCase().replace(/[^a-z]/g, "_")),
        city: "Bengaluru", wants_opd: wantsOPD, wants_tpl: wantsTPL, has_ped: hasPED,
      });
      setResults(r.data);
      setStep("results");
    } catch (e) { show(e.response?.data?.detail || "Could not compare plans"); }
    setLoading(false);
  };

  const toggleCompare = (planId) => {
    setCompareSet((prev) => prev.includes(planId) ? prev.filter((x) => x !== planId) : prev.length < 3 ? [...prev, planId] : prev);
  };

  const bookCall = async () => {
    if (!bookForm.name.trim() || !bookForm.phone.trim()) return show("Name and phone are required");
    try {
      await axios.post(`${API}/insurance/book-call`, {
        ...bookForm, pet_species: species, pet_breed: breed, pet_age: `${ageYears} years`,
      });
      setBooked(true);
      show("Call booked! We'll reach out shortly.");
    } catch (e) { show(e.response?.data?.detail || "Could not book call"); }
  };

  const eligible = results?.eligible || [];
  const comparePlans = eligible.filter((p) => compareSet.includes(p.id));

  return (
    <div data-testid="insurance-page" className="ins-page">
      {/* Hero */}
      {step === "quiz" && (
        <>
          <div className="ins-hero">
            <h1>Smart, Honest Pet Insurance <em>Comparison</em> for Indian Pet Parents</h1>
            <p>Compare Bajaj, HDFC ERGO, Digit & more in 60 seconds. Zero spam, transparent exclusions, real guidance.</p>
          </div>
          <div className="ins-quiz" data-testid="insurance-quiz">
            <div className="ins-step">
              <label className="ins-label">Step 1 — Who are we insuring?</label>
              <div className="ins-chips">
                <button data-testid="ins-species-dog" className={`ins-chip ${species === "dog" ? "selected" : ""}`} onClick={() => setSpecies("dog")}>Dog</button>
                <button data-testid="ins-species-cat" className={`ins-chip ${species === "cat" ? "selected" : ""}`} onClick={() => setSpecies("cat")}>Cat</button>
              </div>
            </div>
            <div className="ins-step">
              <label className="ins-label">Step 2 — Breed</label>
              <div className="ins-breed-wrap">
                <input data-testid="ins-breed-input" className="ins-input" placeholder="Type breed… e.g. Labrador, Indie, Persian" value={breed || breedSearch}
                  onChange={(e) => { setBreedSearch(e.target.value); setBreed(e.target.value); setShowBreedList(true); }}
                  onFocus={() => setShowBreedList(true)} onBlur={() => setTimeout(() => setShowBreedList(false), 200)} />
                {showBreedList && filteredBreeds.length > 0 && (
                  <div className="ins-breed-dropdown">
                    {filteredBreeds.map((b) => (
                      <button key={b} className="ins-breed-option" onMouseDown={() => { setBreed(b); setBreedSearch(""); setShowBreedList(false); }}>{b}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="ins-step">
              <label className="ins-label">Step 3 — Age & Gender</label>
              <input data-testid="ins-age-slider" type="range" className="ins-slider" min="0.25" max="12" step="0.25" value={ageYears} onChange={(e) => setAgeYears(parseFloat(e.target.value))} />
              <div className="ins-age-display">{ageYears < 1 ? `${Math.round(ageYears * 12)} months` : `${ageYears} years`} · {gender === "male" ? "Male" : "Female"}</div>
              <div className="ins-chips" style={{ marginTop: 6 }}>
                <button className={`ins-chip small ${gender === "male" ? "selected" : ""}`} onClick={() => setGender("male")}>Male</button>
                <button className={`ins-chip small ${gender === "female" ? "selected" : ""}`} onClick={() => setGender("female")}>Female</button>
              </div>
            </div>
            <div className="ins-step">
              <label className="ins-label">Step 4 — Health Notes & City</label>
              <div className="ins-chips">
                {SYMPTOM_CHIPS.map((s) => (
                  <button key={s} data-testid={`ins-symptom-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className={`ins-chip ${symptoms.includes(s) ? "selected" : ""}`} onClick={() => toggleSymptom(s)}>{s}</button>
                ))}
              </div>
              <input className="ins-input" value="Bengaluru" readOnly style={{ marginTop: 10, background: "#f8fafc", color: "#64748b" }} />
            </div>
            <button data-testid="ins-compare-btn" className="ins-cta" onClick={runCompare} disabled={loading || !(breed || breedSearch).trim()}>
              {loading ? "Comparing plans…" : "Calculate TailWhisk Score & Find Plans"} <ArrowRight size={18} />
            </button>
            {!breed && <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, textAlign: "center" }}>Select a breed to continue</p>}
          </div>
        </>
      )}

      {/* Results */}
      {step === "results" && results && (
        <>
          <div className="ins-results-header">
            <h2 data-testid="ins-results-title">{eligible.length} plan{eligible.length !== 1 ? "s" : ""} eligible for your {ageYears < 1 ? `${Math.round(ageYears * 12)}-month` : `${ageYears}-year`}-old {breed}</h2>
            <p>Ranked by TailWhisk Score · Coverage 45% · Reliability 35% · Value 20% · Size class: <b>{results.size_class}</b></p>
            <button className="ins-back" onClick={() => { setStep("quiz"); setCompareSet([]); setShowCompare(false); }}>← Edit answers</button>
          </div>

          {hasPED && (
            <div className="ins-honesty-banner" data-testid="ins-ped-warning">
              <AlertTriangle size={16} /> <b>Pre-existing condition declared.</b> Bajaj & HDFC ERGO exclude pre-existing conditions permanently. Generali Central and Universal Sompo cover them after a waiting period. We recommend a free call to discuss.
            </div>
          )}

          <div className="ins-plans">
            {eligible.map((plan, idx) => (
              <div key={plan.id} className={`ins-plan-card ${idx === 0 ? "top" : ""}`} data-testid={`ins-plan-${plan.id}`}>
                {idx === 0 && <div className="ins-top-badge">Recommended</div>}
                <div className="ins-plan-header">
                  <div className="ins-plan-logo" style={{ background: plan.insurer_logo_bg, color: plan.insurer_logo_color }}>{plan.insurer.split(" ")[0]}</div>
                  <div className="ins-plan-info">
                    <h3>{plan.plan_name}</h3>
                    <span className="ins-insurer-name">{plan.insurer}</span>
                  </div>
                  <div className="ins-plan-score">
                    <div className="ins-score-num">{plan.tailwhisk_score}</div>
                    <div className="ins-score-label">TailWhisk Score</div>
                  </div>
                </div>

                <div className="ins-plan-premium">
                  {plan.premium_estimate.available ? plan.premium_estimate.label : plan.premium_estimate.label}
                  <span className="ins-premium-note"> {plan.premium_estimate.note}</span>
                </div>

                <div className="ins-plan-meta">
                  <span>SI: <b>{plan.si_range_label}</b></span>
                  <span>Co-pay: <b>{plan.co_pay_options.length ? `${Math.min(...plan.co_pay_options)}%` : "Varies"}</b></span>
                  <span>Waiting: <b>{plan.waiting_period_days} days</b></span>
                  <span>Model: <b>{plan.claim_model}</b></span>
                </div>

                <div className="ins-badges">
                  {plan.fits_badges.map((b, i) => (
                    <span key={i} className={`ins-badge ${b.type}`}>{b.text}</span>
                  ))}
                </div>

                {plan.uin && <div className="ins-uin">UIN: {plan.uin}</div>}

                <button className="ins-expand" onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}>
                  {expandedPlan === plan.id ? <><ChevronUp size={14} /> Hide details</> : <><ChevronDown size={14} /> Covers, exclusions & add-ons</>}
                </button>

                {expandedPlan === plan.id && (
                  <div className="ins-details">
                    <div className="ins-detail-section">
                      <h4>Base Covers</h4>
                      <ul>{plan.base_covers.map((c, i) => <li key={i}><Check size={12} /> {c}</li>)}</ul>
                    </div>
                    <div className="ins-detail-section">
                      <h4>Key Exclusions</h4>
                      <ul>{plan.exclusions_key.map((c, i) => <li key={i} className="exclusion"><X size={12} /> {c}</li>)}</ul>
                    </div>
                    {plan.recommended_addons.length > 0 && (
                      <div className="ins-detail-section">
                        <h4>Recommended Add-ons for Your Pet</h4>
                        {plan.recommended_addons.map((a, i) => (
                          <div key={i} className="ins-addon"><b>{a.name}</b> — {a.reason}{a.cap ? ` (${a.cap})` : ""}</div>
                        ))}
                      </div>
                    )}
                    <div className="ins-detail-section">
                      <h4>Official Links</h4>
                      <a href={plan.official_urls.product} target="_blank" rel="noreferrer">Product Page →</a>
                      {plan.official_urls.brochure && <a href={plan.official_urls.brochure} target="_blank" rel="noreferrer">Brochure PDF →</a>}
                      {plan.official_urls.wordings && <a href={plan.official_urls.wordings} target="_blank" rel="noreferrer">Policy Wordings →</a>}
                      <a href={plan.official_urls.buy} target="_blank" rel="noreferrer">Get Quote →</a>
                    </div>
                  </div>
                )}

                <div className="ins-plan-actions">
                  <button data-testid={`ins-compare-toggle-${plan.id}`} className={`ins-btn-outline ${compareSet.includes(plan.id) ? "active" : ""}`} onClick={() => toggleCompare(plan.id)}>
                    {compareSet.includes(plan.id) ? "✓ In Compare" : "Add to Compare"}
                  </button>
                  <a className="ins-btn-outline" href={plan.official_urls.wordings || plan.official_urls.product} target="_blank" rel="noreferrer"><FileText size={14} /> Policy Wording</a>
                  <a className="ins-btn-primary" href={plan.official_urls.buy} target="_blank" rel="noreferrer">Get Quote →</a>
                </div>
              </div>
            ))}
          </div>

          {results.ineligible.length > 0 && (
            <div className="ins-ineligible">
              <h3>Not eligible for your pet</h3>
              {results.ineligible.map((p) => (
                <div key={p.id} className="ins-ineligible-row">
                  <b>{p.plan_name}</b> ({p.insurer}) — {p.ineligibility_reasons.join("; ")}
                </div>
              ))}
            </div>
          )}

          {/* Compare bar */}
          {compareSet.length >= 2 && (
            <div className="ins-compare-bar" data-testid="ins-compare-bar">
              <span>{compareSet.length} plans selected</span>
              <button className="ins-cta small" onClick={() => setShowCompare(true)}>Compare Now →</button>
            </div>
          )}

          {/* Compare modal */}
          {showCompare && comparePlans.length >= 2 && (
            <div className="ins-modal-overlay" onClick={() => setShowCompare(false)}>
              <div className="ins-modal ins-compare-modal" onClick={(e) => e.stopPropagation()} data-testid="ins-compare-modal">
                <button className="ins-modal-close" onClick={() => setShowCompare(false)}><X size={20} /></button>
                <h3>Side-by-Side Comparison</h3>
                <div className="ins-table-wrap">
                  <table className="ins-table">
                    <thead><tr><th>Feature</th>{comparePlans.map((p) => <th key={p.id}>{p.plan_name}</th>)}</tr></thead>
                    <tbody>
                      <tr><td><b>TailWhisk Score</b></td>{comparePlans.map((p) => <td key={p.id} style={{ fontFamily: "monospace", fontWeight: 700, color: "#0f766e" }}>{p.tailwhisk_score}/10</td>)}</tr>
                      <tr><td>Premium (indicative)</td>{comparePlans.map((p) => <td key={p.id}>{p.premium_estimate.label}</td>)}</tr>
                      <tr><td>Sum Insured</td>{comparePlans.map((p) => <td key={p.id}>{p.si_range_label}</td>)}</tr>
                      <tr><td>Co-pay</td>{comparePlans.map((p) => <td key={p.id}>{p.co_pay_options.length ? `${Math.min(...p.co_pay_options)}%` : "Varies"}</td>)}</tr>
                      <tr><td>Waiting Period</td>{comparePlans.map((p) => <td key={p.id}>{p.waiting_period_days} days</td>)}</tr>
                      <tr><td>OPD Cover</td>{comparePlans.map((p) => <td key={p.id}>{p.opd.available ? (p.opd.cap ? `✅ Up to ₹${(p.opd.cap / 1000).toFixed(0)}k` : "✅") : "❌"}</td>)}</tr>
                      <tr><td>TPL</td>{comparePlans.map((p) => <td key={p.id}>{p.tpl.available ? `✅ ${p.tpl.label}` : "❌"}</td>)}</tr>
                      <tr><td>Pre-existing (PED)</td>{comparePlans.map((p) => <td key={p.id} style={{ color: p.ped_covered ? "#16a34a" : "#dc2626" }}>{p.ped_covered ? "✅ After waiting" : "❌ Excluded"}</td>)}</tr>
                      <tr><td>Cats</td>{comparePlans.map((p) => <td key={p.id}>{p.species.includes("cat") ? "✅" : "❌"}</td>)}</tr>
                      <tr><td>Claim Model</td>{comparePlans.map((p) => <td key={p.id}>{p.claim_model}</td>)}</tr>
                      <tr><td>UIN</td>{comparePlans.map((p) => <td key={p.id} style={{ fontSize: 11 }}>{p.uin || "N/A"}</td>)}</tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Book call CTA */}
          <div className="ins-book-cta">
            <ShieldCheck size={24} />
            <div>
              <h3>Not sure which plan is right?</h3>
              <p>Talk to a TailWhisk insurance guide — free, no spam, no pressure.</p>
            </div>
            <button data-testid="ins-book-call-btn" className="ins-cta" onClick={() => setShowBookCall(true)}>Book Free Call <Phone size={16} /></button>
          </div>

          {/* Book call modal */}
          {showBookCall && !booked && (
            <div className="ins-modal-overlay" onClick={() => setShowBookCall(false)}>
              <div className="ins-modal" onClick={(e) => e.stopPropagation()} data-testid="ins-book-modal">
                <button className="ins-modal-close" onClick={() => setShowBookCall(false)}><X size={20} /></button>
                <h3>Talk to a TailWhisk Insurance Guide</h3>
                <p className="ins-modal-sub">Free 15-min call. We'll walk you through the best plan for your {breed || "pet"}.</p>
                <input data-testid="ins-book-name" className="ins-input" placeholder="Your name" value={bookForm.name} onChange={(e) => setBookForm({ ...bookForm, name: e.target.value })} />
                <input data-testid="ins-book-email" className="ins-input" placeholder="Email" value={bookForm.email} onChange={(e) => setBookForm({ ...bookForm, email: e.target.value })} />
                <input data-testid="ins-book-phone" className="ins-input" placeholder="Phone" value={bookForm.phone} onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })} />
                <input className="ins-input" value={`Pet: ${breed || "Not selected"}, ${ageYears} yrs`} readOnly style={{ background: "#f8fafc", color: "#64748b" }} />
                <input data-testid="ins-book-date" className="ins-input" type="date" value={bookForm.date} onChange={(e) => setBookForm({ ...bookForm, date: e.target.value })} />
                <input data-testid="ins-book-time" className="ins-input" placeholder="Preferred time (e.g., 2 PM)" value={bookForm.time} onChange={(e) => setBookForm({ ...bookForm, time: e.target.value })} />
                <button data-testid="ins-book-submit" className="ins-cta" onClick={bookCall}>Confirm Free Call →</button>
              </div>
            </div>
          )}
          {booked && (
            <div className="ins-modal-overlay" onClick={() => { setShowBookCall(false); setBooked(false); }}>
              <div className="ins-modal" onClick={(e) => e.stopPropagation()}>
                <Check size={40} color="#16a34a" />
                <h3>Call Booked!</h3>
                <p className="ins-modal-sub">We'll call you at {bookForm.phone} on {bookForm.date || "the next available slot"}. Keep your pet's vaccination records handy.</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Disclaimer */}
      <div className="ins-disclaimer" data-testid="ins-disclaimer">
        Premiums shown are <b>indicative estimates</b> based on publicly available insurer data. TailWhisk is <b>not an IRDAI-licensed broker</b>. Always verify coverage, exclusions & pricing directly with the insurer before purchasing. No cashless pet insurance network exists in India as of 2026 — all plans work on reimbursement.
      </div>
    </div>
  );
}
